import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, fetchJson } from './api';
import { getDashboardNavItems, MANAGEMENT_VIEW_KEYS } from './dashboardNavigation';
import { NotificationBell } from './notifications';
import AccountMenu from './AccountMenu';
import ChatAssistantDrawer from './ChatAssistantDrawer';
import {
    Alert,
    AppBar,
    Avatar,
    Button,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Collapse,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Toolbar,
    Typography,
    useMediaQuery
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ConstructionIcon from '@mui/icons-material/Construction';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';

const drawerExpandedWidth = 288;
const drawerCollapsedWidth = 72;
const drawerMobileWidth = 'min(84vw, 360px)';
const navTransitionMs = 200;

function resolveStaticCompanyName(user) {
    const email = String(user?.email || user?.username || '').trim().toLowerCase();
    const domain = email.includes('@') ? email.split('@')[1] : '';
    const tenantSlug = String(user?.tenant_slug || '').trim().toLowerCase();
    const explicit = String(user?.company || user?.company_name || '').trim();

    if (domain === 'monsurv.com' || domain.endsWith('.monsurv.com')) return 'MonTech Ltd';
    if (
        domain === 'iesisconsult.com' || domain.endsWith('.iesisconsult.com')
        || domain === 'iesisgroup.com' || domain.endsWith('.iesisgroup.com')
    ) return 'Iesis Consult Ltd';
    if (tenantSlug === 'montech') return 'MonTech Ltd';
    if (tenantSlug === 'iesis') return 'Iesis Consult Ltd';
    return explicit || 'Monsurv Ltd';
}

function buildRootUrlWithParams(nextParams, { clearExisting = false } = {}) {
    const url = new URL(window.location.href);
    const qs = clearExisting ? new URLSearchParams() : new URLSearchParams(url.search || '');
    Object.entries(nextParams || {}).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        const s = String(v);
        if (!s) {
            qs.delete(k);
            return;
        }
        qs.set(k, s);
    });
    url.search = qs.toString();
    return url.toString();
}

function goToDashboardView(view, { clearExisting = false, replace = false, tab = '', params = null } = {}) {
    const v = String(view || '').trim();
    const t = String(tab || '').trim();
    const extra = params && typeof params === 'object' ? params : null;

    // Back-compat: workload is now an Analytics tab.
    const nextParams = (v.toLowerCase() === 'workload')
        ? { view: 'analytics', tab: 'workload', ...(extra || {}) }
        : { view: v, ...(t ? { tab: t } : {}), ...(extra || {}) };

    const url = buildRootUrlWithParams(nextParams, { clearExisting });
    try {
        const u = new URL(url);
        const next = `${u.pathname}${u.search}${u.hash}`;
        if (replace) window.history.replaceState(null, '', next);
        else window.history.pushState(null, '', next);
        try {
            window.dispatchEvent(new PopStateEvent('popstate'));
        } catch {
            window.dispatchEvent(new Event('popstate'));
        }
    } catch {
        window.location.assign(url);
    }
}

export default function PortalShell({
    activeKey = '',
    portalMode = '',
    portalReference = '',
    portalPage = '',
    portalInterim = '',
    children
}) {
    const theme = useTheme();
    const isMobileNav = useMediaQuery(theme.breakpoints.down('md'));
    const isPhoneHeader = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);
    const [chatAnchorEl, setChatAnchorEl] = useState(null);
    const [issueOpen, setIssueOpen] = useState(false);
    const [issueTitle, setIssueTitle] = useState('');
    const [issueDescription, setIssueDescription] = useState('');
    const [issuePriority, setIssuePriority] = useState('medium');
    const [issueImage, setIssueImage] = useState(null);
    const [issueSubmitting, setIssueSubmitting] = useState(false);
    const [issueError, setIssueError] = useState('');
    const [issueSuccess, setIssueSuccess] = useState('');
    const [isNavPinned, setIsNavPinned] = useState(() => {
        try {
            return localStorage.getItem('monsurv_sidebar_pinned') === '1';
        } catch {
            return false;
        }
    });
    const [isNavHover, setIsNavHover] = useState(false);
    const [isManagementNavOpen, setIsManagementNavOpen] = useState(() => MANAGEMENT_VIEW_KEYS.includes(activeKey));
    const isNavExpanded = isNavPinned || isNavHover;
    const isManagementView = MANAGEMENT_VIEW_KEYS.includes(activeKey);

    useEffect(() => {
        try {
            localStorage.setItem('monsurv_sidebar_pinned', isNavPinned ? '1' : '0');
        } catch {
            // ignore
        }
    }, [isNavPinned]);

    useEffect(() => {
        if (isManagementView) setIsManagementNavOpen(true);
        else setIsManagementNavOpen(false);
    }, [isManagementView]);

    const [authUser, setAuthUser] = useState(null);

    useEffect(() => {
        async function loadMe() {
            try {
                const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
                if (!token) {
                    setAuthUser(null);
                    return;
                }
                const me = await fetchJson(`${API_BASE_URL}/auth/me`, {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${token}` }
                });
                let mergedUser = me?.user || null;
                try {
                    const capabilitiesJson = await fetchJson(`${API_BASE_URL}/auth/capabilities`, {
                        method: 'GET',
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (mergedUser && capabilitiesJson && typeof capabilitiesJson === 'object') {
                        mergedUser = {
                            ...mergedUser,
                            tenant_slug: String(capabilitiesJson?.tenant_slug || mergedUser?.tenant_slug || '').trim().toLowerCase() || mergedUser?.tenant_slug,
                            tenant_capabilities: Array.isArray(capabilitiesJson?.capabilities)
                                ? capabilitiesJson.capabilities
                                : (Array.isArray(mergedUser?.tenant_capabilities) ? mergedUser.tenant_capabilities : []),
                            feature_visibility: capabilitiesJson?.features && typeof capabilitiesJson.features === 'object'
                                ? capabilitiesJson.features
                                : (mergedUser?.feature_visibility || {})
                        };
                    }
                } catch {
                    // fallback to /auth/me payload
                }
                setAuthUser(mergedUser);
            } catch {
                setAuthUser(null);
            }
        }
        loadMe();
    }, []);

    const authDisplayName = authUser?.display_name || authUser?.displayName || authUser?.username || '';
    const authCompany = resolveStaticCompanyName(authUser);

    const authRole = String(authUser?.role || '').trim().toLowerCase();

    const authPhotoSrc = useMemo(() => {
        const raw = String(authUser?.profile_photo_url || '').trim();
        if (!raw) return '';
        if (/^https?:\/\//i.test(raw)) return raw;
        return `${API_BASE_URL}${raw.startsWith('/') ? raw : `/${raw}`}`;
    }, [authUser?.profile_photo_url]);

    const initials = useMemo(() => {
        const parts = String(authDisplayName || '').trim().split(/\s+/).filter(Boolean);
        if (!parts.length) return 'G';
        const a = parts[0]?.[0] || '';
        const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] || '') : (parts[0]?.[1] || '');
        const out = `${a}${b}`.trim().toUpperCase();
        return out || 'G';
    }, [authDisplayName]);

    const dashboardLinks = useMemo(() => (
        getDashboardNavItems({
            role: authRole,
            portalMode,
            tenantSlug: authUser?.tenant_slug,
            tenantCapabilities: authUser?.tenant_capabilities,
            featureVisibility: authUser?.feature_visibility
        })
    ), [authRole, portalMode, authUser?.tenant_slug, authUser?.tenant_capabilities, authUser?.feature_visibility]);

    const managementNavItems = useMemo(() => (
        dashboardLinks.filter((item) => MANAGEMENT_VIEW_KEYS.includes(item.key))
    ), [dashboardLinks]);

    const topLevelNavItems = useMemo(() => (
        dashboardLinks.filter((item) => !MANAGEMENT_VIEW_KEYS.includes(item.key))
    ), [dashboardLinks]);

    const settingsNavIndex = topLevelNavItems.findIndex((item) => item.key === 'settings');
    const navItemsBeforeSettings = settingsNavIndex >= 0 ? topLevelNavItems.slice(0, settingsNavIndex) : topLevelNavItems;
    const navItemsFromSettings = settingsNavIndex >= 0 ? topLevelNavItems.slice(settingsNavIndex) : [];

    const canReportIssue = useMemo(() => {
        const mode = String(portalMode || '').trim().toLowerCase();
        return mode === 'surveyor' || mode === 'developer';
    }, [portalMode]);
    const shouldExitPortalOnNav = useMemo(() => {
        const mode = String(portalMode || '').trim().toLowerCase();
        // Developer portal uses the sidebar for in-portal navigation.
        // Other portal modes reuse the dashboard sidebar, and should exit portal mode.
        return Boolean(mode) && mode !== 'developer';
    }, [portalMode]);

    const developerNavParams = useMemo(() => {
        const mode = String(portalMode || '').trim().toLowerCase();
        if (mode !== 'developer') return null;
        return { reference: '', page: '', interim: '', workspace: '' };
    }, [portalMode]);

    function navigateToDashboardItem(item) {
        if (item?.external && item?.externalUrl) {
            try {
                window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
            } catch {
                window.location.assign(item.externalUrl);
            }
            if (isMobileNav) setMobileNavOpen(false);
            return;
        }

        const isDevPortal = String(portalMode || '').trim().toLowerCase() === 'developer';
        const nextView = String(item?.view || item?.key || '').trim().toLowerCase();
        const shouldClearProject = isDevPortal && ['overview', 'reports', 'settings'].includes(nextView);
        goToDashboardView(item.view || item.key, {
            clearExisting: shouldExitPortalOnNav,
            params: shouldClearProject ? developerNavParams : null
        });
    }

    function renderDesktopNavItem(item, options = {}) {
        const { nested = false } = options;
        const selected = activeKey === item.key;
        return (
            <ListItemButton
                key={item.key}
                selected={selected}
                onClick={() => {
                    if (isMobileNav) setMobileNavOpen(false);
                    navigateToDashboardItem(item);
                }}
                sx={{
                    mb: 0.5,
                    minHeight: nested ? 38 : 40,
                    justifyContent: isNavExpanded ? 'flex-start' : 'center',
                    alignItems: 'center',
                    width: '100%',
                    px: isNavExpanded ? (nested ? 2.15 : 1.25) : 0.75,
                    ml: isNavExpanded && nested ? 1.25 : 0,
                    borderRadius: 2,
                    transition: theme.transitions.create(['padding-left', 'padding-right', 'background-color'], {
                        duration: navTransitionMs,
                        easing: theme.transitions.easing.easeInOut
                    }),
                    '&.Mui-selected': {
                        backgroundColor: alpha('#ffffff', theme.palette.mode === 'dark' ? 0.08 : 0.10)
                    }
                }}
                aria-label={item.label}
            >
                <ListItemIcon
                    sx={{
                        minWidth: 40,
                        mr: isNavExpanded ? 1 : 0,
                        width: 40,
                        display: 'flex',
                        justifyContent: 'center',
                        color: selected ? theme.palette.primary.main : 'inherit',
                        transition: theme.transitions.create(['min-width', 'margin-right', 'width'], {
                            duration: navTransitionMs,
                            easing: theme.transitions.easing.easeInOut
                        })
                    }}
                >
                    {item.icon}
                </ListItemIcon>
                <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                        fontWeight: selected ? 900 : 650,
                        color: 'inherit',
                        noWrap: true,
                        fontSize: nested ? 13 : undefined
                    }}
                    sx={{
                        m: 0,
                        minWidth: 0,
                        flex: 1,
                        opacity: isNavExpanded ? 1 : 0,
                        maxWidth: isNavExpanded ? 220 : 0,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        transform: isNavExpanded ? 'translateX(0)' : 'translateX(-6px)',
                        transition: theme.transitions.create(['opacity', 'max-width', 'transform'], {
                            duration: navTransitionMs,
                            easing: theme.transitions.easing.easeInOut
                        })
                    }}
                />
            </ListItemButton>
        );
    }

    function renderMobileNavItem(item, options = {}) {
        const { nested = false } = options;
        return (
            <ListItemButton
                key={item.key}
                selected={activeKey === item.key}
                onClick={() => {
                    setMobileNavOpen(false);
                    navigateToDashboardItem(item);
                }}
                sx={{
                    mb: 0.4,
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    borderRadius: 2,
                    px: nested ? 2.15 : 1,
                    py: 0.6,
                    ml: nested ? 1 : 0,
                    minHeight: 36,
                    '&.Mui-selected': {
                        backgroundColor: alpha('#ffffff', theme.palette.mode === 'dark' ? 0.08 : 0.10)
                    }
                }}
                aria-label={item.label}
            >
                <ListItemIcon
                    sx={{
                        minWidth: 30,
                        mr: 0.75,
                        display: 'flex',
                        justifyContent: 'flex-start',
                        '& svg': { fontSize: 18 }
                    }}
                >
                    {item.icon}
                </ListItemIcon>
                <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                        fontSize: nested ? '0.82rem' : '0.86rem',
                        fontWeight: activeKey === item.key ? 900 : 650,
                        noWrap: true
                    }}
                    sx={{ m: 0 }}
                />
            </ListItemButton>
        );
    }

    function openSettingsSection(sectionId) {
        const id = String(sectionId || '').trim();
        if (id) {
            try {
                localStorage.setItem(`monsurv_settings_section_${id}_open`, '1');
            } catch {
                // ignore
            }
        }
        goToDashboardView('settings', { clearExisting: shouldExitPortalOnNav });
    }

    function requestChangePassword() {
        try {
            localStorage.setItem('monsurv_trigger_change_password', '1');
        } catch {
            // ignore
        }
        goToDashboardView('settings', { clearExisting: shouldExitPortalOnNav });
    }

    function handleReportIssue() {
        setIssueError('');
        setIssueSuccess('');
        setIssueOpen(true);
    }

    async function submitIssueReport() {
        if (issueSubmitting) return;
        const title = String(issueTitle || '').trim();
        if (!title) {
            setIssueError('Title is required.');
            return;
        }
        const description = String(issueDescription || '').trim();
        if (!description) {
            setIssueError('Description is required.');
            return;
        }

        setIssueSubmitting(true);
        setIssueError('');
        setIssueSuccess('');

        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            if (!String(token).trim()) throw new Error('Not authenticated');

            const fd = new FormData();
            fd.append('title', title);
            fd.append('description', description);
            fd.append('priority', String(issuePriority || 'medium'));
            fd.append('page', `INFORMATION PORTAL (${String(portalMode || '').toUpperCase() || 'UNKNOWN'}) — ${window.location.pathname}${window.location.search}`);
            if (issueImage) fd.append('image', issueImage);

            const resp = await fetch(`${API_BASE_URL}/issue-report`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: fd
            });
            const json = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(json?.error || 'Failed to submit issue report');

            if (json && json.emailSent === false) {
                setIssueSuccess('Issue report submitted (admin email delivery failed — admins can still view it in Issue Reports).');
            } else {
                setIssueSuccess('Issue report submitted.');
            }
            setIssueTitle('');
            setIssueDescription('');
            setIssuePriority('medium');
            setIssueImage(null);
        } catch (e) {
            setIssueError(e?.message || 'Failed to submit issue report');
        } finally {
            setIssueSubmitting(false);
        }
    }

    function requestReportIssue() {
        handleReportIssue();
    }

    async function signOut() {
        try {
            const token = localStorage.getItem('monsurv_auth_token') || '';
            if (String(token).trim()) {
                await fetch(`${API_BASE_URL}/auth/logout`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => null);
            }
        } catch {
            // ignore
        }
        try { localStorage.removeItem('monsurv_auth_token'); } catch { }
        setAuthUser(null);
        window.location.assign(buildRootUrlWithParams({}, { clearExisting: true }));
    }

    const chatRole = String(portalMode || authUser?.role || '').trim();
    const chatProjectReference = String(portalReference || '').trim();
    const chatReportInstance = useMemo(() => {
        const page = String(portalPage || '').trim().toLowerCase();
        const interim = String(portalInterim || '').trim();
        if (page === 'interim' && interim) return `interim:${interim}`;
        return 'initial';
    }, [portalInterim, portalPage]);

    const drawer = (
        <Box
            sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}
            onMouseEnter={() => setIsNavHover(true)}
            onMouseLeave={() => setIsNavHover(false)}
        >
            <Box
                sx={{
                    p: isNavExpanded ? 2 : 1.25,
                    transition: theme.transitions.create(['padding'], {
                        duration: navTransitionMs,
                        easing: theme.transitions.easing.easeInOut
                    })
                }}
            >
                <Paper
                    variant="outlined"
                    sx={{
                        p: isNavExpanded ? 2 : 1.25,
                        transition: theme.transitions.create(['padding'], {
                            duration: navTransitionMs,
                            easing: theme.transitions.easing.easeInOut
                        }),
                        borderRadius: 3,
                        background:
                            theme.palette.mode === 'dark'
                                ? alpha('#0b1220', 0.6)
                                : alpha('#0b1220', 0.25),
                        borderColor: alpha('#e2e8f0', theme.palette.mode === 'dark' ? 0.10 : 0.18)
                    }}
                >
                    <Stack spacing={1.25}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: 28, minWidth: 0 }}>
                            {isNavExpanded ? (
                                <Typography
                                    variant="subtitle1"
                                    noWrap
                                    sx={{
                                        fontWeight: 900,
                                        color: '#fff',
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    Monsurv Dashboard
                                </Typography>
                            ) : (
                                <Box sx={{ height: 0 }} />
                            )}
                            <IconButton
                                size="small"
                                onClick={() => setIsNavPinned((v) => !v)}
                                aria-label={isNavPinned ? 'Unpin sidebar' : 'Pin sidebar'}
                                sx={{ color: '#fff' }}
                            >
                                {isNavPinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
                            </IconButton>
                        </Stack>
                        <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="center">
                            <Avatar
                                src={authPhotoSrc || undefined}
                                sx={{
                                    width: 44,
                                    height: 44,
                                    bgcolor: alpha('#60a5fa', 0.25),
                                    border: `1px solid ${alpha('#e2e8f0', 0.18)}`
                                }}
                            >
                                {initials}
                            </Avatar>
                        </Stack>
                        {isNavExpanded ? (
                            <>
                                <Typography variant="body2" sx={{ textAlign: 'center', color: alpha('#ffffff', 0.92), fontWeight: 800 }}>
                                    Hi, {authDisplayName ? authDisplayName : 'Guest'}
                                </Typography>
                                <Typography variant="caption" sx={{ textAlign: 'center', color: alpha('#ffffff', 0.78) }}>
                                    {String(authCompany || 'Monsurv Ltd')}
                                </Typography>
                            </>
                        ) : null}
                    </Stack>
                </Paper>
            </Box>

            <Divider sx={{ borderColor: alpha('#e2e8f0', theme.palette.mode === 'dark' ? 0.10 : 0.14) }} />

            <List
                sx={{
                    px: isNavExpanded ? 1 : 0.75,
                    py: 1,
                    transition: theme.transitions.create(['padding-left', 'padding-right'], {
                        duration: navTransitionMs,
                        easing: theme.transitions.easing.easeInOut
                    })
                }}
            >
                {navItemsBeforeSettings.map((item) => renderDesktopNavItem(item))}

                {managementNavItems.length ? (
                    <>
                        <ListItemButton
                            selected={isManagementView}
                            onClick={() => {
                                if (isManagementView) return;
                                setIsManagementNavOpen((open) => !open);
                            }}
                            sx={{
                                mb: 0.5,
                                minHeight: 40,
                                justifyContent: isNavExpanded ? 'flex-start' : 'center',
                                alignItems: 'center',
                                width: '100%',
                                px: isNavExpanded ? 1.25 : 0.75,
                                borderRadius: 2,
                                transition: theme.transitions.create(['padding-left', 'padding-right', 'background-color'], {
                                    duration: navTransitionMs,
                                    easing: theme.transitions.easing.easeInOut
                                }),
                                '&.Mui-selected': {
                                    backgroundColor: alpha('#ffffff', theme.palette.mode === 'dark' ? 0.08 : 0.10)
                                }
                            }}
                            aria-label="Management"
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 40,
                                    mr: isNavExpanded ? 1 : 0,
                                    width: 40,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    color: isManagementView ? theme.palette.primary.main : 'inherit',
                                    transition: theme.transitions.create(['min-width', 'margin-right', 'width'], {
                                        duration: navTransitionMs,
                                        easing: theme.transitions.easing.easeInOut
                                    })
                                }}
                            >
                                <BusinessCenterIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Management"
                                primaryTypographyProps={{
                                    fontWeight: isManagementView ? 900 : 650,
                                    color: 'inherit',
                                    noWrap: true
                                }}
                                sx={{
                                    m: 0,
                                    minWidth: 0,
                                    flex: 1,
                                    opacity: isNavExpanded ? 1 : 0,
                                    maxWidth: isNavExpanded ? 220 : 0,
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    transform: isNavExpanded ? 'translateX(0)' : 'translateX(-6px)',
                                    transition: theme.transitions.create(['opacity', 'max-width', 'transform'], {
                                        duration: navTransitionMs,
                                        easing: theme.transitions.easing.easeInOut
                                    })
                                }}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: isNavExpanded ? 20 : 0,
                                    opacity: isNavExpanded ? 1 : 0,
                                    overflow: 'hidden',
                                    transform: isManagementNavOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                    transition: theme.transitions.create(['opacity', 'width', 'transform'], {
                                        duration: navTransitionMs,
                                        easing: theme.transitions.easing.easeInOut
                                    })
                                }}
                            >
                                <ExpandMoreIcon fontSize="small" />
                            </Box>
                        </ListItemButton>

                        <Collapse in={isManagementNavOpen && isNavExpanded} timeout="auto" unmountOnExit>
                            <List disablePadding sx={{ display: 'grid', gap: 0.35, pt: 0.25 }}>
                                {managementNavItems.map((item) => renderDesktopNavItem(item, { nested: true }))}
                            </List>
                        </Collapse>
                    </>
                ) : null}

                {navItemsFromSettings.map((item) => renderDesktopNavItem(item))}
            </List>

            <Box sx={{ flex: 1 }} />
            <Box
                sx={{
                    p: isNavExpanded ? 2 : 1,
                    display: 'flex',
                    justifyContent: 'center',
                    transition: theme.transitions.create(['padding'], {
                        duration: navTransitionMs,
                        easing: theme.transitions.easing.easeInOut
                    })
                }}
            >
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        background:
                            theme.palette.mode === 'dark'
                                ? alpha('#0b1220', 0.6)
                                : alpha('#0b1220', 0.22),
                        borderColor: alpha('#e2e8f0', theme.palette.mode === 'dark' ? 0.10 : 0.18),
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: isNavExpanded ? '100%' : 48,
                        maxWidth: '100%',
                        transition: theme.transitions.create(['width'], {
                            duration: navTransitionMs,
                            easing: theme.transitions.easing.easeInOut
                        })
                    }}
                >
                    <Stack spacing={1.25}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ justifyContent: isNavExpanded ? 'flex-start' : 'center' }}>
                            {isNavExpanded ? <SmartToyIcon fontSize="small" style={{ color: '#fff' }} /> : null}
                            {isNavExpanded ? (
                                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: '#fff' }}>
                                    Ask MonsurvAI
                                </Typography>
                            ) : null}
                        </Stack>
                        <Button
                            fullWidth={isNavExpanded}
                            variant="contained"
                            onClick={(e) => {
                                setChatAnchorEl(e.currentTarget);
                                setChatOpen(true);
                            }}
                            sx={{
                                fontWeight: 900,
                                bgcolor: alpha('#ffffff', 0.14),
                                color: '#fff',
                                '&:hover': { bgcolor: alpha('#ffffff', 0.18) },
                                minWidth: 0,
                                px: isNavExpanded ? 2 : 0,
                                py: isNavExpanded ? 1 : 0,
                                borderRadius: isNavExpanded ? 999 : 2,
                                width: isNavExpanded ? '100%' : 40,
                                height: isNavExpanded ? 'auto' : 40,
                                display: 'flex',
                                justifyContent: 'center',
                                alignSelf: isNavExpanded ? 'stretch' : 'center',
                                lineHeight: 1,
                                transition: theme.transitions.create(['width', 'height', 'padding', 'border-radius'], {
                                    duration: navTransitionMs,
                                    easing: theme.transitions.easing.easeInOut
                                }),
                                '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0 }
                            }}
                        >
                            {isNavExpanded ? 'Start Chat' : <SmartToyIcon fontSize="small" style={{ margin: 0 }} />}
                        </Button>
                        {canReportIssue ? (
                            <Button
                                fullWidth={isNavExpanded}
                                variant="outlined"
                                onClick={requestReportIssue}
                                sx={{
                                    fontWeight: 800,
                                    color: '#fff',
                                    borderColor: alpha('#ffffff', 0.4),
                                    '&:hover': { borderColor: alpha('#ffffff', 0.7), bgcolor: alpha('#ffffff', 0.08) },
                                    minWidth: 0,
                                    px: isNavExpanded ? 2 : 0,
                                    py: isNavExpanded ? 1 : 0,
                                    borderRadius: isNavExpanded ? 999 : 2,
                                    width: isNavExpanded ? '100%' : 40,
                                    height: isNavExpanded ? 'auto' : 40,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignSelf: isNavExpanded ? 'stretch' : 'center',
                                    lineHeight: 1,
                                    transition: theme.transitions.create(['width', 'height', 'padding', 'border-radius'], {
                                        duration: navTransitionMs,
                                        easing: theme.transitions.easing.easeInOut
                                    }),
                                    '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0 }
                                }}
                            >
                                <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            opacity: isNavExpanded ? 0 : 1,
                                            transition: 'opacity 150ms ease',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <ConstructionIcon fontSize="small" />
                                    </Box>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        sx={{
                                            opacity: isNavExpanded ? 1 : 0,
                                            transition: 'opacity 150ms ease',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        <ConstructionIcon fontSize="small" />
                                        <Box component="span">Report An Issue</Box>
                                    </Stack>
                                </Box>
                            </Button>
                        ) : null}
                    </Stack>
                </Paper>
            </Box>
        </Box>
    );

    const drawerMobile = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 1.25, pt: 1.25, pb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                    <Avatar
                        src={authPhotoSrc || undefined}
                        sx={{
                            width: 34,
                            height: 34,
                            bgcolor: alpha('#60a5fa', 0.25),
                            border: `1px solid ${alpha('#e2e8f0', 0.18)}`,
                            // If no profile photo (e.g. shows initials like "AD"), shrink text for comfortable fit.
                            fontSize: authPhotoSrc ? undefined : '0.72rem',
                        }}
                    >
                        {initials}
                    </Avatar>
                    <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ fontWeight: 900, minWidth: 0, color: 'inherit' }}
                        title={authDisplayName || 'Guest'}
                    >
                        {authDisplayName ? authDisplayName : 'Guest'}
                    </Typography>
                </Stack>
            </Box>

            <Divider sx={{ borderColor: alpha('#e2e8f0', theme.palette.mode === 'dark' ? 0.10 : 0.14) }} />

            <List sx={{ px: 0.75, py: 0.75 }}>
                {navItemsBeforeSettings.map((item) => renderMobileNavItem(item))}

                {managementNavItems.length ? (
                    <>
                        <ListItemButton
                            selected={isManagementView}
                            onClick={() => {
                                if (isManagementView) return;
                                setIsManagementNavOpen((open) => !open);
                            }}
                            sx={{
                                mb: 0.4,
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                borderRadius: 2,
                                px: 1,
                                py: 0.6,
                                minHeight: 36,
                                '&.Mui-selected': {
                                    backgroundColor: alpha('#ffffff', theme.palette.mode === 'dark' ? 0.08 : 0.10)
                                }
                            }}
                            aria-label="Management"
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 30,
                                    mr: 0.75,
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    '& svg': { fontSize: 18 }
                                }}
                            >
                                <BusinessCenterIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Management"
                                primaryTypographyProps={{
                                    fontSize: '0.86rem',
                                    fontWeight: isManagementView ? 900 : 650,
                                    noWrap: true
                                }}
                                sx={{ m: 0 }}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transform: isManagementNavOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                                    transition: theme.transitions.create(['transform'], {
                                        duration: navTransitionMs,
                                        easing: theme.transitions.easing.easeInOut
                                    })
                                }}
                            >
                                <ExpandMoreIcon fontSize="small" />
                            </Box>
                        </ListItemButton>

                        <Collapse in={isManagementNavOpen} timeout="auto" unmountOnExit>
                            <List disablePadding sx={{ display: 'grid', gap: 0.35, pt: 0.1 }}>
                                {managementNavItems.map((item) => renderMobileNavItem(item, { nested: true }))}
                            </List>
                        </Collapse>
                    </>
                ) : null}

                {navItemsFromSettings.map((item) => renderMobileNavItem(item))}
            </List>

            <Box sx={{ flex: 1 }} />

            <Box sx={{ px: 0.75, pb: 1.25 }}>
                <List sx={{ p: 0 }}>
                    <ListItemButton
                        onClick={(e) => {
                            setChatAnchorEl(e.currentTarget);
                            setChatOpen(true);
                        }}
                        sx={{
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            borderRadius: 2,
                            px: 1,
                            py: 0.6,
                            minHeight: 36,
                            backgroundColor: alpha('#ffffff', theme.palette.mode === 'dark' ? 0.06 : 0.08)
                        }}
                        aria-label="MonsurvAI"
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 30,
                                mr: 0.75,
                                display: 'flex',
                                justifyContent: 'flex-start',
                                '& svg': { fontSize: 18 }
                            }}
                        >
                            <SmartToyIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                            primary="MonsurvAI"
                            primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 500, noWrap: true }}
                            sx={{ m: 0 }}
                        />
                    </ListItemButton>

                    {canReportIssue ? (
                        <ListItemButton
                            onClick={() => {
                                setMobileNavOpen(false);
                                handleReportIssue();
                            }}
                            sx={{
                                mt: 0.75,
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                borderRadius: 2,
                                px: 1,
                                py: 0.6,
                                minHeight: 36,
                                backgroundColor: alpha('#ffffff', theme.palette.mode === 'dark' ? 0.06 : 0.08)
                            }}
                            aria-label="Report An Issue"
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 30,
                                    mr: 0.75,
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    '& svg': { fontSize: 18 }
                                }}
                            >
                                <ConstructionIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary="Report An Issue"
                                primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 650, noWrap: true }}
                                sx={{ m: 0 }}
                            />
                        </ListItemButton>
                    ) : null}
                </List>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: 'calc(var(--app-vh, 1vh) * 100)', bgcolor: 'background.default' }}>
            <AppBar
                position="fixed"
                color="transparent"
                elevation={0}
                sx={{
                    // Keep the Drawer above the top bar.
                    zIndex: (t) => t.zIndex.drawer - 1,
                    backdropFilter: 'blur(10px)',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? alpha('#0b1220', 0.55)
                            : alpha('#ffffff', 0.65)
                }}
            >
                <Toolbar sx={{ minHeight: { xs: 44, sm: 56 }, px: { xs: 1, sm: 2 } }}>
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={() => setMobileNavOpen(true)}
                        size="small"
                        sx={{ mr: { xs: 0.5, sm: 1 }, display: { md: 'none' }, width: { xs: 28, sm: 36 }, height: { xs: 28, sm: 36 } }}
                    >
                        <MenuIcon sx={{ fontSize: { xs: 18, sm: 22 } }} />
                    </IconButton>

                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                            Monsurv
                        </Typography>
                    </Box>

                    {(authUser || String(portalMode || '').trim().toLowerCase() === 'developer') ? (
                        <Stack direction="row" alignItems="center" sx={{ gap: { xs: 0.5, sm: 1 } }}>
                            {authUser ? <NotificationBell compact={isPhoneHeader} /> : null}
                            <AccountMenu
                                displayName={authDisplayName || (portalMode ? 'Developer' : 'Account')}
                                position={authUser?.position || ''}
                                role={String(authUser?.role || portalMode || '').toLowerCase()}
                                profilePhotoUrl={authUser?.profile_photo_url || null}
                                compact={isPhoneHeader}
                                menuVariant={authUser ? 'full' : 'minimal'}
                                onOpenSettingsSection={openSettingsSection}
                                onChangePassword={requestChangePassword}
                                onSignOut={signOut}
                            />
                        </Stack>
                    ) : null}
                </Toolbar>
            </AppBar>

            <Box
                component="nav"
                sx={{
                    width: { md: isNavExpanded ? drawerExpandedWidth : drawerCollapsedWidth },
                    flexShrink: { md: 0 },
                    transition: { md: theme.transitions.create(['width'], { duration: navTransitionMs, easing: theme.transitions.easing.easeInOut }) },
                    willChange: { md: 'width' }
                }}
                aria-label="Navigation"
            >
                <Drawer
                    variant="temporary"
                    open={mobileNavOpen}
                    onClose={() => setMobileNavOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        display: { xs: 'block', md: 'none' },
                        '& .MuiDrawer-paper': { width: drawerMobileWidth },
                    }}
                >
                    {drawerMobile}
                </Drawer>

                <Drawer
                    variant="permanent"
                    open
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        '& .MuiDrawer-paper': {
                            width: isNavExpanded ? drawerExpandedWidth : drawerCollapsedWidth,
                            boxSizing: 'border-box',
                            overflowX: 'hidden',
                            transition: theme.transitions.create(['width'], {
                                duration: navTransitionMs,
                                easing: theme.transitions.easing.easeInOut
                            }),
                            willChange: 'width'
                        },
                    }}
                >
                    {drawer}
                </Drawer>
            </Box>

            <ChatAssistantDrawer
                open={chatOpen}
                onClose={() => setChatOpen(false)}
                anchorEl={chatAnchorEl}
                displayName={authDisplayName}
                role={chatRole}
                portalMode={portalMode}
                projectReference={chatProjectReference}
                reportInstance={chatReportInstance}
                onNavigate={(nav) => {
                    if (isMobileNav) setMobileNavOpen(false);
                    setChatOpen(false);
                    const view = typeof nav === 'string' ? nav : String(nav?.view || '').trim();
                    const section = typeof nav === 'object' && nav ? String(nav?.section || '').trim() : '';
                    const tab = typeof nav === 'object' && nav ? String(nav?.tab || '').trim().toLowerCase() : '';
                    if (!view) return;
                    if (view === 'settings' && section) {
                        openSettingsSection(section);
                        return;
                    }
                    goToDashboardView(view, { tab });
                }}
            />

            <Dialog open={issueOpen} onClose={() => setIssueOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Report an Issue</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <TextField
                            fullWidth
                            label="Issue Title"
                            value={issueTitle}
                            onChange={(e) => setIssueTitle(e.target.value)}
                            placeholder="Title"
                            helperText="Summarize the issue in one line."
                            inputProps={{ maxLength: 200 }}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '6px !important' },
                                '& .MuiOutlinedInput-notchedOutline': { borderRadius: '6px !important' },
                                '& .MuiOutlinedInput-root fieldset': { borderRadius: '6px !important' }
                            }}
                        />
                        <TextField
                            select
                            fullWidth
                            label="Priority"
                            value={issuePriority}
                            onChange={(e) => setIssuePriority(e.target.value)}
                            SelectProps={{ native: true }}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '6px !important' },
                                '& .MuiOutlinedInput-notchedOutline': { borderRadius: '6px !important' },
                                '& .MuiOutlinedInput-root fieldset': { borderRadius: '6px !important' }
                            }}
                        >
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                        </TextField>
                        <TextField
                            fullWidth
                            label="Issue Description"
                            multiline
                            minRows={5}
                            value={issueDescription}
                            onChange={(e) => setIssueDescription(e.target.value)}
                            placeholder="Describe the Issue"
                            helperText="Include steps to reproduce and what you expected to happen."
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '6px !important' },
                                '& .MuiOutlinedInput-notchedOutline': { borderRadius: '6px !important' },
                                '& .MuiOutlinedInput-root fieldset': { borderRadius: '6px !important' }
                            }}
                        />
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                                Upload an Image of the error (optional)
                            </Typography>
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(e) => {
                                    const f = e.target.files && e.target.files[0];
                                    setIssueImage(f || null);
                                }}
                            />
                        </Box>
                        {issueError ? <Alert severity="error">{issueError}</Alert> : null}
                        {issueSuccess ? <Alert severity="success">{issueSuccess}</Alert> : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIssueOpen(false)} disabled={issueSubmitting}>
                        Close
                    </Button>
                    <Button variant="contained" onClick={submitIssueReport} disabled={issueSubmitting}>
                        {issueSubmitting ? 'Submitting…' : 'Submit Issue Report'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
                <Toolbar />
                {children}
            </Box>
        </Box>
    );
}
